import { useMutation, useQueryClient } from '@tanstack/react-query';

import { postHasuraGraphql } from '../../../../utils/hasuraGraphql';
import { useToast } from '../../../Toast/context/useToast.context';

import { setupOnlinePublicationQueryKeys } from './setupOnlinePublicationQueryKeys';

// Built as a single mutation containing one `update_..._by_pk` call per
// ordered story id. Codegen can't type a query whose shape changes per call,
// so this stays as a raw string handed to `postHasuraGraphql`.
// TODO(BUD-XXXXX): replace with `update_publicationNavigationMenuStories_many`.
const buildReorderMenuStoriesMutation = (orderedStoriesIds: string[]): string => `
  mutation reorderMenuStoriesList {
    ${orderedStoriesIds
      .map(
        (menuStoryId, index) => `
      story${index}: update_publicationNavigationMenuStories_by_pk(
        _set: { order: ${index + 1} }
        pk_columns: { id: "${menuStoryId}" }
      ) {
        id
        order
      }`
      )
      .join('')}
  }
`;

export type UseOrganizeMenuStoriesListMessages = {
  successTitle: string;
  successBody?: string;
  errorTitle: string;
  errorBody?: string;
};

export type OrganizeMenuStoriesListParams = {
  orderedStoriesIds: string[];
  menuId: string;
};

export function useOrganizeMenuStoriesList(messages: UseOrganizeMenuStoriesListMessages) {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();

  return useMutation<
    Record<string, { id: string; order: number }>,
    Error,
    OrganizeMenuStoriesListParams
  >({
    mutationKey: [...setupOnlinePublicationQueryKeys.all, 'organizeMenuStoriesList'],
    mutationFn: ({ orderedStoriesIds }) =>
      postHasuraGraphql<Record<string, { id: string; order: number }>>(
        buildReorderMenuStoriesMutation(orderedStoriesIds),
        {}
      ),
    onSuccess: (_data, { menuId }) => {
      void queryClient.invalidateQueries({
        queryKey: setupOnlinePublicationQueryKeys.menuStories({ menuId })
      });
      toggleToast({
        title: messages.successTitle,
        body: messages.successBody,
        severity: 'success'
      });
    },
    onError: () =>
      toggleToast({
        title: messages.errorTitle,
        body: messages.errorBody,
        severity: 'error'
      })
  });
}
